export const INSTALLED_BASE_BASE = '/installedBase';

/**
 * Helpers for the path-segment based installed base URLs.
 *
 * The selected node path is encoded directly in the pathname:
 *   [null, "14342", "87082"]  <->  /installedBase/14342/87082
 *   [null]                    <->  /installedBase
 * The leading `null` (root level) is implicit in the URL.
 * `search`/`filter` stay as JSON query params, but empty maps are omitted
 * so the reset target URL is exactly /installedBase.
 * `detail` marks the node detail panel as open for the deepest selected
 * node; its value is the active tab (?detail=information). Risk report
 * detail views live on their own route (/riskAssessmentReports/:reportId) —
 * that route is the deep-link target for notifications.
 */

export const DETAIL_TABS = ['information', 'contact', 'documents', 'riskReports', 'networkScan'] as const;
export type DetailTab = typeof DETAIL_TABS[number];

// "?detail=documents" -> "documents"; unknown values (stale/foreign links)
// fall back to the first tab; absent param -> null (panel closed).
export function parseDetailTab(currentSearch: string): DetailTab | null {
    const raw = new URLSearchParams(currentSearch).get('detail');
    if (!raw) return null;
    return (DETAIL_TABS as readonly string[]).includes(raw) ? (raw as DetailTab) : DETAIL_TABS[0];
}

// "14342/87082" | "" -> [null, "14342", "87082"] | [null]
export function parseSelectedFromSplat(splat: string | undefined): (string | null)[] {
    const segments = (splat ?? '')
        .split('/')
        .filter(Boolean)
        .map(decodeURIComponent);
    return [null, ...segments];
}

// [null, "14342", "87082"] -> "/installedBase/14342/87082"; [null] -> "/installedBase"
export function buildInstalledBasePathname(selected: (string | null)[]): string {
    const ids = (selected ?? []).filter(
        (id): id is string => typeof id === 'string' && id.length > 0,
    );
    if (ids.length === 0) return INSTALLED_BASE_BASE;
    return `${INSTALLED_BASE_BASE}/${ids.map(encodeURIComponent).join('/')}`;
}

// Builds the query string: preserves unknown params, drops the legacy
// `selected` and `report` params, sets `search`/`filter` only when the maps
// are non-empty and `detail` only while the detail panel is open.
export function buildInstalledBaseSearch(
    currentSearch: string,
    searchMap: Record<string, unknown>,
    filterMap: Record<string, unknown>,
    detailTab: DetailTab | null = null,
): string {
    const params = new URLSearchParams(currentSearch);
    params.delete('selected');
    // Legacy: reports briefly lived at ?detail=riskReports&report=<id>; the
    // detail view moved to /riskAssessmentReports/:reportId.
    params.delete('report');
    if (searchMap && Object.keys(searchMap).length > 0) {
        params.set('search', JSON.stringify(searchMap));
    } else {
        params.delete('search');
    }
    if (filterMap && Object.keys(filterMap).length > 0) {
        params.set('filter', JSON.stringify(filterMap));
    } else {
        params.delete('filter');
    }
    if (detailTab) {
        params.set('detail', detailTab);
    } else {
        params.delete('detail');
    }
    const result = params.toString();
    return result ? `?${result}` : '';
}

// Legacy links: ?selected=[null,"14342",...] -> [null, "14342", ...],
// or null if the param is absent or malformed.
export function parseLegacySelected(currentSearch: string): (string | null)[] | null {
    try {
        const raw = new URLSearchParams(currentSearch).get('selected');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        const ids = parsed.filter(
            (id): id is string => typeof id === 'string' && id.length > 0,
        );
        return [null, ...ids];
    } catch {
        return null;
    }
}
